import axios, { AxiosRequestConfig } from 'axios';
import Company, { LoadCompanySummaryResponse } from '../models/Company';
import CookbookModel from '../models/cookbook';
import { CookbookMenuDestinationsResponse, CookbookMenuGroup } from '../models/CookbookMenuDestination';
import {
	CountEquipmentFilterType,
	Equipment,
	EquipmentCleaningExecution,
	LoadEquipmentByCompanyData,
	UpdateEquipmentRequestType
} from '../models/equipment';
import User, { CreateUserRequestType, CreateUserTypeRequestType, UserRecoverMenuModel } from '../models/User';
import UserConfigs from '../models/UserConfigs';
import { readonlyApi } from './api-readonly';

import { LoggedUser } from '../db/db';
import { ErrorLogsModel } from '../models/ErrorLogs';
import { AddLocationRequestType, AddLocationResponseType, UpdateLocationRequestType } from '../models/Location';
import { RecipeExecution } from '../models/Recipe';
import RequestServiceModel from '../models/RequestService';
import {
	AddStoreRequestType,
	AddStoreResponseType,
	CountEquipmentData,
	UpdateStoreRequestType
} from '../models/Stores';
import UserRelationshipTutorial from '../models/UserRelationshipTutorials';
import { terminateIokSession } from '../integrations/iokSessionLifecycle';
import {
	queuePendingCsatSurveyTrigger,
	ADD_RECIPE_CSAT_TRIGGER_CONTEXT
} from '../utils/csat';

export const config: AxiosRequestConfig = {
	baseURL: process.env.REACT_APP_API_URL,
	responseType: 'json',
	headers: {
		accept: 'application/json',
		'content-type': 'application/json',
	},
	timeout: 7200000,
	withCredentials: false,
	maxRedirects: 5,
	validateStatus: (status: number) => status >= 200 && status < 300,
	transformResponse: (response) => {
		if (response !== null && response.error) {
			console.error(response.error);
		}
		let resp;
		try {
			resp = JSON.parse(response);
		} catch {
			resp = response;
		}
		return resp;
	},

};

const queueAddRecipeCsatTrigger = (
	contextType: string,
	contextId: number | string | null | undefined
) => {
	if (contextId == null) {
		return;
	}

	queuePendingCsatSurveyTrigger({
		triggerContext: ADD_RECIPE_CSAT_TRIGGER_CONTEXT,
		contextType,
		contextId: String(contextId),
	});
};

axios.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => {
		if (error.response && error.response.data && (error.response.data.error === "Access denied!" || error.response.data.error === "jwt expired")) {
			void terminateIokSession({ reason: 'expired', publicPath: '/' });
		}
		return Promise.reject(error);
	}
);

export const createHeaders = (params: { accessToken: string, contentType?: string }) => {
	const { accessToken, contentType = 'application/json' } = params;

	return {
		...config.headers,
		'x-access-token': accessToken,
		'content-type': contentType,
	}
};

interface UserData {
	user: User
	configs: UserConfigs
	company: Company
}

/********************************************** USER && CONFIG *********************************************/

export const login = async (userInfo: any) => {
	config.url = 'login'
	config.method = 'post'
	config.data = userInfo
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

export const authenticationPassword = async (userInfo: any, accessToken: string) => {
	return axios.request<any>({
		...config,
		url: 'authenticationPassword',
		method: 'post',
		data: userInfo,
		headers: {
			...config.headers,
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
};
export const createDeveloperToken = async (userInfo: any, accessToken: string) => {
	config.url = 'developerToken'
	config.method = 'post'
	config.data = userInfo
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

export const signup = async (content: any) => {
	config.url = 'signup'
	config.method = 'post'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const signupRecoverUser = async (content: any) => {
	config.url = 'signup/recover'
	config.method = 'post'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const loadUserByCorporateName = async (corporateName: string) => {
	config.url = `/load/user/${corporateName}`
	config.method = 'get'
	config.params = corporateName
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const resetPasswordEmail = async (content: any) => {
	config.url = 'resetPassword/sendEmail'
	config.method = 'post'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const resetAccountEmail = async (content: any) => {
	config.url = 'resetAccount/sendEmail'
	config.method = 'post'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const activateAccount = async (activateToken: any) => {
	config.url = 'activate'
	config.method = 'put'
	config.data = activateToken
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const recuseUser = async (content: any) => {
	config.url = `recuse/user/${content.id}/userName/${content.userName}/companyName/${content.companyName}/email/${content.email}`;
	config.method = 'delete';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-activate-token': content.activateToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const recuseDeveloperToken = async (email: string, accessToken: string) => {
	config.url = `resetDeveloperToken/${email}`;
	config.method = 'put';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const recoverMenu = async (content: UserRecoverMenuModel, activateToken: string) => {
	config.url = 'recover/menu'
	config.method = 'post'
	config.timeout = 2400000
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-activate-token': activateToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const checkIfUserExists = async (email: string) => {
	const filter = { where: { email: email } };
	config.url = `users?filter=${JSON.stringify(filter)}`;
	config.method = 'get';
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const loadCpfcnpj = async (cpfcnpj: any) => {
	config.url = `company/load/cpfcnpj`
	config.method = 'get'
	config.params = cpfcnpj
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const getUserData = async (accessToken: string) => {
	config.url = 'loadUser'
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return await axios.request<UserData>(config)
};

export const loadUserByCompany = async (
	id: any,
	accessToken: string,
	pageNumber?: number,
	limitResult?: number,
	searchTerm?: string
) => {
	const queryParams: Record<string, string> = {};
	if (pageNumber) {
		queryParams.pageNumber = String(pageNumber);
	};
	if (limitResult) {
		queryParams.limitResult = String(limitResult);
	};
	if (searchTerm) {
		queryParams.searchTerm = searchTerm;
	};

	config.url = `company/${id}/user`
	config.method = 'get'
	config.params = queryParams
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
};

export const loadCompanyById = async (id: any) => {
	config.url = `company/${id}`
	config.method = 'get'
	config.params = id
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
};

export const deleteUser = async (id: number, accessToken: string) => {
	config.url = `user/${id}`;
	config.method = 'delete';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request<any>(config);
};

export const editAccount = async (content: any, accessToken: string, signal?: AbortSignal) => {
	return axios.request<any>({
		baseURL: process.env.REACT_APP_API_URL,
		url: 'editUser',
		method: 'put',
		signal,
		data: content,
		headers: {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
}

export type ChangeAccountEmailResponse = {
	requiresActivation: true;
	activationEmailSent: boolean;
};

export const changeAccountEmail = async (content: {
	newEmail: string;
	currentPassword: string;
	userName: string;
	phone: string;
}, accessToken: string) => {
	return axios.request<ChangeAccountEmailResponse>({
		...config,
		url: 'account/email',
		method: 'put',
		data: content,
		headers: {
			...config.headers,
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
}

export const resendAccountActivation = async (email: string) => {
	return axios.request<{ accepted: true }>({
		...config,
		url: 'account/email/resend-activation',
		method: 'post',
		timeout: 30000,
		data: { email },
		headers: {
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
}

export const sendHeartbeat = async (accessToken: string) => {
	return axios.request<any>({
		...config,
		url: 'user/me/heartbeat',
		method: 'post',
		data: undefined,
		headers: {
			...config.headers,
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
}

export const editUserResetPasswordAccount = async (content: any, activateToken: string) => {
	config.url = `editUser/resetPassword`
	config.method = 'put'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-activate-token': activateToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const updateConfigUser = async (content: any, accessToken: string) => {
	config.url = `configUser/${content.userConfigs.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const createUser = async (content: CreateUserRequestType, accessToken: string) => {
	config.url = 'user'
	config.method = 'post'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

export const getPreviousUser = async (email: string) => {
	config.url = `loadUser/${email}`
	config.method = 'get'
	config.data = email
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

export const getTutorials = async (idUser: number, accessToken: string) => {
	config.url = `tutorials/${idUser}`
	config.method = 'get'
	config.params = idUser
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

export const deleteTutorials = async (idUser: number, accessToken: string) => {
	config.url = `/deleteTutorials/${idUser}`;
	config.method = 'delete';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request(config);
};

export const postUserRelationshipTutorials = async (data: UserRelationshipTutorial) => {
	config.url = `userTutorial`
	config.method = 'post'
	config.data = data
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

/********************************************** EQUIPMENT *********************************************/

export const loadEquipByMenu = async (id: any, accessToken: string) => {
	config.url = `equipment/menuId/${id}`
	config.method = 'get'
	config.params = id
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export interface LoadEquipmentParamsType {
	user: LoggedUser;
	accessToken: string;
	lastSeenId?: number;
	limitResult?: number;
	searchTerm?: string;
	filter?: Record<string, any>;
}

export const loadEquipmentByCompanyId = async ({
	user, accessToken, lastSeenId, limitResult, searchTerm, filter
}: LoadEquipmentParamsType) => {
	let url = `user/${user.id}/privilege/${user.userTypeId}/equipment/${user.companyId}/company`;

	const queryParams: Record<string, string> = {};

	if (lastSeenId !== undefined && lastSeenId !== null) {
		queryParams.lastSeenId = String(lastSeenId);
	}
	if (limitResult) {
		queryParams.limitResult = String(limitResult);
	}
	if (searchTerm) {
		queryParams.searchTerm = searchTerm;
	}
	if (filter && Object.keys(filter).length !== 0) {
		queryParams.filter = JSON.stringify(filter);
	}

	const queryString = new URLSearchParams(queryParams).toString();
	if (queryString) {
		url += `?${queryString}`;
	}

	config.url = url;
	config.method = 'get';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'accept': 'application/json',
			'content-type': 'application/json'
		});
	}

	return axios.request<LoadEquipmentByCompanyData[]>(config);
};

export const loadEquipments = async (accessToken: string) => {
	config.url = `equipment/all`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any[]>(config)
}

export const loadCountEquipment = async (
	accessToken: string,
	fieldFilter?: CountEquipmentFilterType
) => {
	if (fieldFilter) {
		const key = Object.keys(fieldFilter)[0];
		config.url = `equipment/count?where[${key}]=${fieldFilter[key]}`;
	} else {
		config.url = 'equipment/count';
	}

	config.method = 'get';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request<CountEquipmentData>(config);
};

export const createEquipment = async (
	data: any,
	accessToken: string
) => {
	config.url = '/linux-equipments/create-new-linux/DE@Prat1c@BR2021';
	config.method = 'post';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	config.data = data;
	return axios.request<Equipment>(config);
};

export const deleteEquipment = async (id: number, accessToken: string) => {
	config.url = `equipment/${id}`;
	config.method = 'delete';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request<LoadEquipmentByCompanyData[]>(config);
};

export const updateEquipment = async (id: number, content: UpdateEquipmentRequestType, accessToken: string) => {
	config.url = `equipment/${id}`;
	config.method = 'put'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

export const loadEquipmentById = async (id: number | string, accessToken: string) => {
	config.url = `equipment/${id}`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<Equipment>(config)
};

export const loadEquipmentStatusBySerialNumber = async (serialNUmber: string, accessToken: string) => {
	config.url = `equipmentStatus/serialNumber/${serialNUmber}`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
};

export const loadEquipmentType = async (accessToken: string) => {
	config.url = `equipmentType/0`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
};

/********************************************** END EQUIPMENT *********************************************/

// Menu

export const createMenu = async (content: any, accessToken: string) => {
	config.url = `menu`
	config.method = 'post'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const convertMenu = async (
	content: {
		dataToConvert: { menuId: number, sourceEquipTypeId: number, targetEquipTypeId: number }
	},
	accessToken: string
) => {
	config.url = `/menu/${content.dataToConvert.menuId}/convert`
	config.method = 'post'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content;
	return axios.request<any>(config);
};

export const updateMenu = async (content: any, accessToken: string) => {
	config.url = `menu/${content.menu.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'content-type': 'application/json',
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const deleteMenu = async (id: number, accessToken: string) => {
	config.url = `menu/${id}`
	config.method = 'delete'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

export const loadCookbookMenuDestinations = (
	idCompany: number,
	accessToken: string,
	equipmentTypeId: number,
	pageNumber: number,
	searchTerm: string,
	signal?: AbortSignal
) => {
	const query = new URLSearchParams({
		equipmentTypeId: String(equipmentTypeId),
		pageNumber: String(pageNumber),
		limitResult: '20'
	});
	if (searchTerm.trim()) query.set('searchTerm', searchTerm.trim());
	return axios.request<CookbookMenuDestinationsResponse>({
		baseURL: process.env.REACT_APP_API_URL,
		url: `company/${idCompany}/cookbook-menu-destinations?${query.toString()}`,
		method: 'get',
		headers: createHeaders({ accessToken }),
		signal
	});
};

export const loadCookbookMenuDestinationGroups = (
	menuId: number,
	accessToken: string,
	signal?: AbortSignal
) => axios.request<{ groups: CookbookMenuGroup[] } | undefined>({
	baseURL: process.env.REACT_APP_API_URL,
	url: `menu/${menuId}/group`,
	method: 'get',
	headers: createHeaders({ accessToken }),
	signal
});

export const loadMenuByCompanyId = async (idCompany: number, accessToken: string) => {
	config.url = `company/${idCompany}/menu`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadMenuByCompanyIdWithCursor = async (
	idCompany: number,
	accessToken: string,
	limitResult?: number,
	pageNumber?: number,
	searchTerm?: string
) => {
	let url = `company/${idCompany}/menu`;
	const queryParams: Record<string, string> = {};
	if (pageNumber) {
		queryParams.pageNumber = String(pageNumber);
	};
	if (limitResult) {
		queryParams.limitResult = String(limitResult);
	};
	if (searchTerm) {
		queryParams.searchTerm = searchTerm;
	};

	const queryString = new URLSearchParams(queryParams).toString();
	if (queryString) {
		url += `?${queryString}`;
	};

	config.url = url;
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadMenuTrashByCompanyId = async (
	idCompany: number,
	accessToken: string,
	limitResult?: number,
	pageNumber?: number,
	searchTerm?: string
) => {
	let url = `company/${idCompany}/menuTrash`;
	const queryParams: Record<string, string> = {};
	if (pageNumber) {
		queryParams.pageNumber = String(pageNumber);
	};
	if (limitResult) {
		queryParams.limitResult = String(limitResult);
	};
	if (searchTerm) {
		queryParams.searchTerm = searchTerm;
	};

	const queryString = new URLSearchParams(queryParams).toString();
	if (queryString) {
		url += `?${queryString}`;
	};

	config.url = url
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadCompanySummaryById = async (idCompany: number, accessToken: string) => {
	config.url = `company/${idCompany}/summary`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<LoadCompanySummaryResponse>(config)
}

export const loadMenuAll = async (id: number, accessToken: string) => {
	config.url = `menu/${id}`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadInfoMenus = async (companyId: number, accessToken: string) => {
	config.url = `menus/${companyId}`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadInfoMenusWithGroups = async (companyId: number, accessToken: string, equipTypeId?: number) => {
	let url = `company/${companyId}/menus-groups`;
	if (equipTypeId) url += `?equipmentTypeId=${equipTypeId}`

	const localConfig: AxiosRequestConfig = {
		...config,
		url,
		method: 'get',
		params: undefined,
		data: undefined,
		headers: createHeaders({ accessToken })
	};

	return axios.request<any>(localConfig)
}

// Group

export const createGroup = async (content: any, accessToken: string) => {
	config.url = `group`
	config.method = 'post'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const copyGroups = async (
	content: { userCompanyId: number, groupIds: number[], menuIds: number[] }, accessToken: string
) => {
	config.url = `copy-groups`;
	config.method = 'post';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content;
	return axios.request<any>(config);
};

export const loadGroups = async (id: any, accessToken: string) => {
	config.url = `menu/${id}/group`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const editGroup = async (content: any, accessToken: string) => {
	config.url = `group/${content.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const deleteGroup = async (
	content: { groupIds: number[] },
	accessToken: string
) => {
	config.url = 'groups'
	config.method = 'delete'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

// Recipe

export const createRecipe = async (content: any, accessToken: string) => {
	config.url = `recipe`
	config.method = 'post'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	const response = await axios.request<any>(config);
	queueAddRecipeCsatTrigger('recipe', response.data?.[0]?.id);
	return response;
};

export const loadRecipes = async (id: any, accessToken: string) => {
	config.url = `group/${id}/recipe`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadSimpleRecipes = async (id: any, accessToken: string) => {
	config.url = `menu/${id}/simpleRecipe`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const createSimpleRecipe = async (content: any, accessToken: string) => {
	config.url = `simpleRecipe`
	config.method = 'post'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	const response = await axios.request<any>(config);
	queueAddRecipeCsatTrigger('simpleRecipe', response.data?.[0]?.id);
	return response;
}

export const copyRecipes = async (
	content: { equipTypeId: number, recipeIds: number[], destinations: { menuId: number, groupId?: number }[] | number[] },
	accessToken: string
) => {
	config.url = `copy-recipes`
	config.method = 'post'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const moveRecipes = async (
	content: { equipTypeId: number, recipeIds: number[], destinations: { menuId: number, groupId?: number } | number },
	accessToken: string
) => {
	config.url = `move-recipes`
	config.method = 'put'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
}

export const updateSimpleRecipe = async (content: any, accessToken: string) => {
	config.url = `simpleRecipe/${content.recipe.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
}

export const deleteSimpleRecipes = async (content: any, accessToken: string) => {
	config.url = `simpleRecipe/array`
	config.method = 'delete'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const editRecipe = async (content: any, accessToken: string) => {
	config.url = `recipe/${content.recipe.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
}

export const deleteRecipes = async (content: any, accessToken: string) => {
	config.url = `recipe/array`
	config.method = 'delete'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

interface AnalyticsParamsModel {
	equipmentId: number;
	accessToken: string;
	startDate?: string;
	endDate?: string;
	lastSeenTimestamp?: string;
	limit?: number;
	includeExecutions?: boolean;
	errorIds?: number[];
	origin?: 'oven' | 'proofer' | 'equipment';
}

type DataTypeMap = {
	recipes: RecipeExecution[];
	events: ErrorLogsModel[];
	cleanings: EquipmentCleaningExecution[];
};

export const fetchAnalyticalDataFromEquipment = async <T extends keyof DataTypeMap>(
	dataType: T,
	params: AnalyticsParamsModel
) => {
	const { equipmentId, accessToken, startDate, endDate, lastSeenTimestamp, limit, includeExecutions, errorIds, origin } = params;

	const endpoints = {
		recipes: '/recipeExecutions',
		events: '/errorLogs',
		cleanings: '/cleaningExecution'
	};

	config.url = endpoints[dataType];
	config.method = 'get';
	config.params = {
		equipmentId,
		...(startDate && endDate ? { startDate, endDate } : {}),
		...(lastSeenTimestamp ? { lastSeenTimestamp } : {}),
		...(limit || limit === 0 ? { limit } : {}),
		...(includeExecutions ? { includeExecutions } : {}),
		...(errorIds !== undefined ? { errorIds } : {}),
		...(origin ? { origin } : {}),
	};

	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}

	return axios.request<DataTypeMap[T]>(config);
};

export const loadErrorMaintenanceLogsListByEquipmentId = async (equipmentId: any, serialNumber: string, accessToken: string) => {
	config.url = `/errorLogs/maintenance/equipmentId/${equipmentId}serialNumber/${serialNumber}`
	config.method = 'get'
	config.params = equipmentId
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<ErrorLogsModel[]>(config)
}

export const loadErrorMaintenanceLogsListByDates = async (equipmentId: any, serialNumber: string, startDate: string, endDate: string, accessToken: string) => {
	config.url = `/errorLogs/maintenance/equipmentId/${equipmentId}/serialNumber/${serialNumber}/startDate/'${startDate}'/endDate/'${endDate}'`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<ErrorLogsModel[]>(config)
}

export const deleteErrorLogsMaintenance = async (equipmentId: number, accessToken: string) => {
	config.url = `/errorLogs/maintenance/equipmentId/${equipmentId}`
	config.method = 'delete'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}


// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

// Lojas

export const addNewStore = async (
	store: AddStoreRequestType,
	accessToken: string
) => {
	config.url = 'store';
	config.method = 'post';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	config.data = {
		store: {
			...store,
		},
	};
	return axios.request<AddStoreResponseType>(config);
};

export const deleteStore = async (id: number, accessToken: string) => {
	config.url = `store/${id}`;
	config.method = 'delete';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request(config);
};

export const loadStoreById = async (id: number, accessToken: string) => {
	config.url = `store/${id}`;
	config.method = 'get';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request<any>(config);
};

export const updateStore = async (content: UpdateStoreRequestType, accessToken: string) => {
	config.url = `store/${content.store.id}`;
	config.method = 'put';
	config.data = content;
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request(config);
};

/* request que busca o CEP na API dos Correios */

export const getZipCodeInfo = async (zipCode: string) => {
	const configZipCode: AxiosRequestConfig = {
		baseURL: 'https://viacep.com.br/ws/',
		responseType: 'json',
		headers: {
			accept: 'application/json',
			'content-type': 'application/json',
		},
		timeout: 40000,
		withCredentials: false,
		maxRedirects: 5,
		validateStatus: (status: number) => status >= 200 && status < 300,
		transformResponse: (response) => {
			if (response !== null && response.error) {
				console.error(response.error);
			}
			const resp = JSON.parse(response);
			return resp;
		},
	};

	configZipCode.url = `${zipCode}/json/`;
	configZipCode.method = 'get';
	return axios.request(configZipCode);
};

// Cookbook

export const createCookbook = async (content: { cookbook: CookbookModel }, accessToken: string) => {
	config.url = `cookbook`
	config.method = 'post'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	const response = await axios.request<any>(config);
	queueAddRecipeCsatTrigger('cookbookRecipe', response.data?.id);
	return response;
};

export const loadCookbookRecipes = async (
	id: any,
	accessToken: string,
	pageNumber?: number,
	limitResult?: number,
	searchTerm?: string,
	filters?: {
		equipmentType?: string;
		mainIngredient?: string;
		typeDish?: string;
		languageRecipe?: string;
		createdByCurrentUser?: boolean;
	}
) => {
	let url = `company/${id}/cookbook`;
	const queryParams: Record<string, string> = {};
	if (pageNumber) {
		queryParams.pageNumber = String(pageNumber);
	};
	if (limitResult) {
		queryParams.limitResult = String(limitResult);
	};
	if (searchTerm) {
		queryParams.searchTerm = searchTerm;
	};
	if (filters) {
		Object.entries(filters).forEach(([key, value]) => {
			if (value !== undefined && value !== '') queryParams[key] = String(value);
		});
	}

	const queryString = new URLSearchParams(queryParams).toString();
	if (queryString) {
		url += `?${queryString}`;
	};
	config.url = url
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
};

export const loadCookbookRecipesById = async (id: number, accessToken: string) => {
	config.url = `/cookbook/${id}`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const editCookbook = async (content: any, accessToken: string) => {
	config.url = `cookbook/${content.cookbook.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const deleteCookbook = async (content: any, accessToken: string) => {
	config.url = `cookbook/array`
	config.method = 'delete'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

// Steps Speed Oven

export const createStepSpeedOven = async (content: any, accessToken: string) => {
	config.url = `stepSpeedOven`
	config.method = 'post'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const loadStepsSpeedOven = async (id: any, stepFrom: string, accessToken: string) => {
	config.url = `/${stepFrom}/recipe/${id}/stepsSpeedOven`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const editStepSpeedOven = async (content: any, accessToken: string) => {
	config.url = `stepSpeedOven/${content.stepSpeedOven.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const deleteStepSpeedOven = async (id: any, accessToken: string) => {
	config.url = `stepSpeedOven/${id}`
	config.method = 'delete'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

// Steps Combi Oven TSi 

export const createStepCombiOvenTSI = async (content: any, accessToken: string) => {
	config.url = `stepCombiOvenTSI`
	config.method = 'post'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const loadStepsCombiOvenTSI = async (id: any, stepFrom: string, accessToken: string) => {
	config.url = `/${stepFrom}/recipe/${id}/stepsCombiOvenTSI`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const updateStepCombiOvenTSI = async (content: any, accessToken: string) => {
	config.url = `stepCombiOvenTSI/${content.stepCombiOvenTSI.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const deleteStepCombiOvenTSI = async (id: any, accessToken: string) => {
	config.url = `stepCombiOvenTSI/${id}`
	config.method = 'delete'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

// Steps Combi Oven CMAX

export const createStepCombiOvenCMAX = async (content: any, accessToken: string) => {
	config.url = `stepCombiOvenCMAX`
	config.method = 'post'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const loadStepsCombiOvenCMAX = async (id: any, stepFrom: string, accessToken: string) => {
	config.url = `${stepFrom}/recipe/${id}/stepsCombiOvenCMAX`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const updateStepCombiOvenCMAX = async (content: any, accessToken: string) => {
	config.url = `stepCombiOvenCMAX/${content.stepCombiOvenCMAX.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const deleteStepCombiOvenCMAX = async (id: any, accessToken: string) => {
	config.url = `stepCombiOvenCMAX/${id}`
	config.method = 'delete'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

//Atualização de software

export const downloadUpdate = async (ovenType: string, accessToken: string) => {
	config.url = `/download/platform/updateFile/${ovenType}`
	config.method = 'get'
	config.responseType = 'blob'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return { downloadFile: axios.request<any>(config), source: axios.CancelToken.source() };
};

export const loadCompanyTypes = async () => {
	config.url = `/companyTypes`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadCompaniesDistribuidorPratica = async (accessToken: string) => {
	config.url = `/company/companyType/0`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadPermissionByCompanyType = async (companyTypeId: string, userTypeId: number, accessToken: string) => {
	config.url = `permissionTypes/companyTypeId/"${companyTypeId}"/userTypeId/${userTypeId}`;
	config.method = 'get';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request<any>(config);
};

export const createUserType = async (content: CreateUserTypeRequestType, accessToken: string) => {
	config.url = 'userTypes'
	config.method = 'post'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

export const loadUserTypesByCompany = async (companyId: number, accessToken: string) => {
	config.url = `userTypes/companyId/${companyId}`;
	config.method = 'get';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request<any>(config);
};

export const updateCompanyNewConnect = async (content: any, accessToken: string) => {
	config.url = '/company/newConnect'
	config.method = 'put'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

/********************************************** EXPORT MENU *********************************************/

export const exportSpeedOvens = async (equipmentModel: string, menuId: number, accessToken: string) => {
	const readOnlySpeedOvensModels = new Set(['COPA, ROCKET, COPA SM', 'FORZA', 'FIT', 'FIT XL', 'FIT ST', 'FIT SM', 'FIT LITE']);

	if (readOnlySpeedOvensModels.has(equipmentModel)) {
		return readonlyApi.get(`speed-ovens-file/export/${equipmentModel}/${menuId}`, {
			responseType: 'blob',
			headers: {
				'x-access-token': accessToken,
				accept: 'application/json',
				'content-type': 'application/json'
			}
		});
	}

	config.url = `speed-ovens-file/export/${equipmentModel}/${menuId}`;
	config.method = 'get';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	config.responseType = 'blob';
	var resp = axios.request(config);
	config.responseType = 'json';
	return resp;
};

export const exportTsi = async (menuId: number, accessToken: string) => {
	return readonlyApi.get(`tsi-file/export/${menuId}`, {
		responseType: 'blob',
		headers: {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
};

export const exportCMaxEvo = async (menuId: number, accessToken: string) => {
	return readonlyApi.get(`c-max-evo-file/export/${menuId}`, {
		responseType: 'blob',
		headers: {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
};

export const exportCMax = async (menuId: number, equipType: number, accessToken: string) => {
	return readonlyApi.get(`c-max-file/export/${menuId}/equipType/${equipType}`, {
		responseType: 'blob',
		headers: {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
};

export const exportConvection = async (menuId: number, temperatureUnit: string, accessToken: string) => {
	return readonlyApi.get(`convection-file/export/${menuId}/temperatureUnit/${temperatureUnit}`, {
		responseType: 'blob',
		headers: {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
};

export const exportLastroToro = async (menuId: number, accessToken: string) => {
	return readonlyApi.get(`lastro-toro-file/export/${menuId}`, {
		responseType: 'blob',
		headers: {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
};

export const exportLastroToroTC = async (menuId: number, accessToken: string) => {
	return readonlyApi.get(`lastro-toro-tc-file/export/${menuId}`, {
		responseType: 'blob',
		headers: {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
};

export const exportBakingStation = async (menuId: number, accessToken: string) => {
	return readonlyApi.get(`baking-station-file/export/${menuId}`, {
		responseType: 'blob',
		headers: {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
};


export const exportForza = async (menuId: number, accessToken: string) => {
	return readonlyApi.get(`forza-file/export/${menuId}`, {
		responseType: 'blob',
		headers: {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
};

export const exportSpeedOvensLegacy = async (generation: string, menuId: number, accessToken: string) => {
	return readonlyApi.get(`speed-ovens-legacy/export/${generation}/${menuId}`, {
		responseType: 'blob',
		headers: {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		}
	});
};

export const getMetabaseUrl = async (idDashboard: number, accessToken: string) => {
	config.url = `get-metabase-url/${idDashboard}`;
	config.method = 'get';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request<any>(config);
};

export const loadAdmByEquipmentType = async (equipmentType: string, accessToken: string) => {
	config.url = `load/user/equipmentType/${equipmentType}`
	config.method = 'get'
	config.params = equipmentType
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
};

export const addSoftwareVersion = async (
	formData: any,
	accessToken: string
) => {
	config.url = `add-software-version`;
	config.method = 'post';
	config.data = formData;
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
		});
	}
	return await axios.request(config);
};

/************************************* IMPORT MENU ******************************************/

export const importSpeedOvens = async (
	formData: FormData,
	accessToken: string
) => {
	config.url = `speed-ovens-file/import`;
	config.method = 'post';
	config.data = formData;
	config.responseType = 'text' as 'json';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'accept': 'application/json',
			'content-type': 'multipart/form-data',
		});
	}
	return await axios.request(config);
};

export const importTSi = async (
	formData: FormData,
	accessToken: string
) => {
	config.url = `tsi-file/import`;
	config.method = 'post';
	config.data = formData;
	config.responseType = 'text' as 'json';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'accept': 'application/json',
			'content-type': 'multipart/form-data',
		});
	}
	return await axios.request(config);
};

export const importCMAXEvo = async (
	formData: FormData,
	accessToken: string
) => {
	config.url = `c-max-evo-file/import`;
	config.method = 'post';
	config.data = formData;
	config.responseType = 'text' as 'json';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'accept': 'application/json',
			'content-type': 'multipart/form-data',
		});
	}
	return await axios.request(config);
};

export const importCMAX = async (
	formData: FormData,
	accessToken: string
) => {
	config.url = `c-max-file/import`;
	config.method = 'post';
	config.data = formData;
	config.responseType = 'text' as 'json';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'accept': 'application/json',
			'content-type': 'multipart/form-data',
		});
	}
	return await axios.request(config);
};

export const importLastroToro = async (
	formData: FormData,
	accessToken: string
) => {
	config.url = `lastro-toro-file/import`;
	config.method = 'post';
	config.data = formData;
	config.responseType = 'text' as 'json';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'accept': 'application/json',
			'content-type': 'multipart/form-data',
		});
	}
	return await axios.request(config);
};

export const importLastroToroTC = async (
	formData: FormData,
	accessToken: string
) => {
	config.url = `lastro-toro-tc-file/import`;
	config.method = 'post';
	config.data = formData;
	config.responseType = 'text' as 'json';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'accept': 'application/json',
			'content-type': 'multipart/form-data',
		});
	}
	return await axios.request(config);
};

export const importBakingStation = async (
	formData: FormData,
	accessToken: string
) => {
	config.url = `baking-station-file/import`;
	config.method = 'post';
	config.data = formData;
	config.responseType = 'text' as 'json';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'accept': 'application/json',
			'content-type': 'multipart/form-data',
		});
	}
	return await axios.request(config);
};

export const importForza = async (
	formData: FormData,
	accessToken: string
) => {
	config.url = `forza-legacy-file/import`;
	config.method = 'post';
	config.data = formData;
	config.responseType = 'text' as 'json';
	if (config.headers) {
		Object.assign(config.headers, {
			'accept': 'application/json',
			'content-type': 'multipart/form-data',
		});
	}
	return await axios.request(config);
};

export const importSpeedOvensLegacy = async (
	formData: FormData,
	accessToken: string
) => {
	config.url = `copa-legacy-file/import`;
	config.method = 'post';
	config.data = formData;
	config.responseType = 'text' as 'json';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'accept': 'application/json',
			'content-type': 'multipart/form-data',
		});
	}
	return await axios.request(config);
};

export const importConvectionOven = async (
	formData: FormData,
	accessToken: string
) => {
	config.url = `convection-file/import`;
	config.method = 'post';
	config.data = formData;
	config.responseType = 'text' as 'json';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			'accept': 'application/json',
			'content-type': 'multipart/form-data',
		});
	}
	return await axios.request(config);
};

export const loadSoftwareVersion = async (versionType: string, accessToken: string) => {
	config.url = `softwareVersion/'${versionType}'`
	config.method = 'get'
	config.params = versionType
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
};

export const sendEmailMergeAccounts = async (content: any, accessToken: string) => {
	config.url = 'sendEmail/company/merge'
	config.method = 'post'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

export const updateMergeAccounts = async (content: any, accessToken: string) => {
	config.url = 'company/merge'
	config.method = 'put'
	config.data = content
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

/************************************* STEP LASTRO TORO ******************************************/

export const createStepLastroToro = async (content: any, accessToken: string) => {
	config.url = `stepLastroToro`
	config.method = 'post'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const loadByIdStepsLastroToro = async (id: any, accessToken: string) => {
	config.url = `stepLastroToro/${id}`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadStepsLastroToro = async (recipeId: any, stepFrom: string, accessToken: string) => {
	config.url = `${stepFrom}/recipe/${recipeId}/stepsLastroToro`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const updateStepLastroToro = async (content: any, accessToken: string) => {
	config.url = `stepLastroToro/${content.stepLastroToro.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const deleteStepLastroToro = async (id: any, accessToken: string) => {
	config.url = `stepLastroToro/${id}`
	config.method = 'delete'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

/************************************* STEP CONVECCTION OVENS ******************************************/

export const createStepConvectionOven = async (content: any, accessToken: string) => {
	config.url = `stepConvectionOven`
	config.method = 'post'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const loadByIdStepsConvecctionOven = async (id: any, accessToken: string) => {
	config.url = `stepConvecctionOven/${id}`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadStepsConvectionOven = async (recipeId: any, stepFrom: string, accessToken: string) => {
	config.url = `${stepFrom}/recipe/${recipeId}/stepsConvectionOven`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const updateStepConvectionOven = async (content: any, accessToken: string) => {
	config.url = `stepConvectionOven/${content.stepConvectionOven.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const deleteStepConvectionOven = async (id: any, accessToken: string) => {
	config.url = `stepConvectionOven/${id}`
	config.method = 'delete'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

// LocalizaÃ§Ã£o

export const addLocation = async (
	location: AddLocationRequestType,
	accessToken: string
) => {
	config.url = 'location';
	config.method = 'post';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	config.data = {
		location: {
			...location,
		},
	};
	return axios.request<AddLocationResponseType>(config);
};

export const deleteLocation = async (id: number, accessToken: string) => {
	config.url = `location/${id}`;
	config.method = 'delete';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request(config);
};

export const loadLocationById = async (id: number, accessToken: string) => {
	config.url = `location/${id}`;
	config.method = 'get';
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request<any>(config);
};

// export const loadLocationsByCompanyId = async (
// 	// user: LoggedUser,
// 	companyId: number,
// 	accessToken: string
// ) => {
// 	config.url = `company/${companyId}/locations`;
// 	config.method = 'get';
// 	if (config.headers) {
// 		Object.assign(config.headers, {
// 			'x-access-token': accessToken,
// 			accept: 'application/json',
// 			'content-type': 'application/json'
// 		});
// 	}
// 	return axios.request<LoadLocationByCompanyData>(config);
// };

export const updateLocation = async (content: UpdateLocationRequestType, accessToken: string) => {
	config.url = `location/${content.location.id}`;
	config.method = 'put';
	config.data = content;
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request(config);
};

export const requestService = async (
	content: RequestServiceModel,
	accessToken: string
) => {
	config.url = `requestService`;
	config.method = 'post';
	config.data = content;
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
		});
	}
	return await axios.request(config);
};

export const loadEquipmentSold = async () => {
	config.baseURL = 'https://testeprp.praticabr.com:3335/api/'
	config.url = `Sb1010s/getTopModels?models=
	001035,
	001096,
	001143,
	001146,
	001041,
	001042,
	001043,
	001044,
	001045,
	001046,
	001047,
	001048,
	001049,
	001050,
	001126,
	001247,
	000948,
	001012,
	001013,`;
	config.method = 'get';
	if (config.headers) {
		Object.assign(config.headers, {
			'apiKey': 'Pr@t1Ã§aPr0dut0s2025',
			accept: 'application/json',
			'content-type': 'application/json'
		});
	}
	return axios.request<any>(config);
};

/* export const downloadFile = async (
	fileType: string,
	fileFormat: string,
	accessToken: string,
	onProgress: (progress: number) => void
) => {
	const normalizedFileType = encodeURIComponent(fileType);

	try {
		const response = await axios.get(`/download/updateFile/${normalizedFileType}`, {
			baseURL: process.env.REACT_APP_API_URL,
			headers: { 'x-access-token': accessToken },
			responseType: 'blob',
			onDownloadProgress: (progressEvent) => {
				const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
				onProgress(percentCompleted);
			},
		});

		const url = window.URL.createObjectURL(new Blob([response.data]));
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', fileFormat);
		link.target = '_self';
		document.body.appendChild(link);
		link.click();

		setTimeout(() => {
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		}, 1000);

		return true;
	} catch (error) {
		console.error('Error downloading file:', error);
		return false;
	}
}; */

export const downloadFile = async (
	fileType: string,
	fileFormat: string,
	accessToken: string,
	onProgress: (progress: number) => void
) => {
	const apiUrl = `${process.env.REACT_APP_API_URL}download/platform/updateFile/${fileType}`;

	try {
		const response = await axios.get(apiUrl, {
			headers: { 'x-access-token': accessToken },
			responseType: 'blob',
			onDownloadProgress: (progressEvent) => {
				const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
				onProgress(percentCompleted);
			},
		});

		const url = window.URL.createObjectURL(new Blob([response.data]));
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', fileFormat);
		link.target = '_self';
		document.body.appendChild(link);
		link.click();

		setTimeout(() => {
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		}, 1000);

		return true;
	} catch (error) {
		console.error('Error downloading file:', error);
		return false;
	}
};

/************************************* STEP BAKING STATION ******************************************/

export const createStepEquipment = async (content: any, accessToken: string) => {
	config.url = `stepEquipment`
	config.method = 'post'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const loadByIdStepsEquipment = async (id: any, accessToken: string) => {
	config.url = `stepEquipment/${id}`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const loadStepsEquipment = async (recipeId: any, stepFrom: string, accessToken: string) => {
	config.url = `${stepFrom}/recipe/${recipeId}/stepsEquipment`
	config.method = 'get'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config)
}

export const updateStepEquipment = async (content: any, accessToken: string) => {
	const stepData = content.stepEquipment ?? content.stepLastroToro;

	if (!stepData?.id) {
		throw new Error('StepEquipment id not provided');
	}

	config.url = `stepEquipment/${stepData.id}`
	config.method = 'put'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	config.data = content
	return axios.request<any>(config);
};

export const deleteStepEquipment = async (id: any, accessToken: string) => {
	config.url = `stepEquipment/${id}`
	config.method = 'delete'
	if (config.headers) {
		Object.assign(config.headers, {
			'x-access-token': accessToken,
			accept: 'application/json',
			'content-type': 'application/json'
		})
	}
	return axios.request<any>(config);
};

